/**
 * SupportWidget — headless support-ticket popup for AgenticEcom_SupportTickets.
 *
 * Two ways to open the same popup:
 *   1. Floating button (bottom-left / bottom-right per admin `position`), shown
 *      automatically when popup is enabled. Mounted once, globally, via Layout.
 *   2. CMS-block trigger — drop ANY element with class `agentic-support-trigger`
 *      OR attribute `data-ast-support` into a CMS block (a link, a button, plain
 *      text). Clicking it opens the popup. e.g.
 *         <button class="agentic-support-trigger">Need help?</button>
 *
 * All settings (button text, heading, intro, accent colour, departments, guest
 * toggle, reCAPTCHA key) come from the `supportConfig` GraphQL query, so the
 * merchant configures it in Magento admin and the storefront follows. Accent
 * colour falls back to the active theme's --color-primary, so it adopts the
 * theme preset automatically when the admin leaves it blank.
 */
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { graphqlRequest } from '@agentic/magento-client/lib/graphql-client';
import { gql } from '@agentic/magento-client/lib/graphql-mini';
import { showSuccess, showError } from '@agentic/magento-client/stores/toast';

interface Department { id: number; name: string; requires_order?: boolean | null; }
interface SupportConfig {
  enabled: boolean;
  allow_guests: boolean;
  popup_enabled: boolean;
  button_text?: string | null;
  heading?: string | null;
  intro?: string | null;
  position?: string | null;
  accent_color?: string | null;
  show_on_mobile?: boolean | null;
  recaptcha_site_key?: string | null;
  departments?: Department[] | null;
}

const CONFIG_QUERY = gql`
  query SupportConfig {
    supportConfig {
      enabled allow_guests popup_enabled button_text heading intro position
      accent_color show_on_mobile recaptcha_site_key
      departments { id name requires_order }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation CreateSupportTicket($input: CreateSupportTicketInput!) {
    createSupportTicket(input: $input) { success message reference }
  }
`;

// minimal per-key reCAPTCHA v3 loader (the support form carries its own key,
// independent of the global recaptchaV3Config)
interface Grecaptcha { ready: (cb: () => void) => void; execute: (k: string, o: { action: string }) => Promise<string>; }
let scriptPromise: Promise<boolean> | null = null;
function loadRecaptcha(key: string): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  const w = window as unknown as { grecaptcha?: Grecaptcha };
  if (w.grecaptcha) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<boolean>((resolve) => {
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(key)}`;
    s.async = true; s.defer = true;
    s.onload = () => resolve(!!(window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
  return scriptPromise;
}
async function getRecaptchaToken(key: string): Promise<string | null> {
  try {
    const ok = await loadRecaptcha(key);
    const w = window as unknown as { grecaptcha?: Grecaptcha };
    if (!ok || !w.grecaptcha) return null;
    await new Promise<void>((r) => w.grecaptcha!.ready(() => r()));
    return await w.grecaptcha.execute(key, { action: 'support' });
  } catch { return null; }
}

/** Higher-contrast of black/white on a hex accent (WCAG). Falls back to white
 *  when the accent isn't a literal hex (e.g. a `var(--color-primary)` string). */
function readableOnAccent(accent: string): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(accent.trim());
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  const L = 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
  // contrast vs white = 1.05/(L+0.05); vs black = (L+0.05)/0.05
  return (L + 0.05) / 0.05 >= 1.05 / (L + 0.05) ? '#111827' : '#ffffff';
}

export default function SupportWidget() {
  const [cfg, setCfg] = useState<SupportConfig | null>(null);
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ department_id: '', subject: '', message: '', name: '', email: '', phone: '' });

  useEffect(() => {
    setMounted(true);
    graphqlRequest<{ supportConfig: SupportConfig | null }>(CONFIG_QUERY, undefined, { silent: true })
      .then((d) => setCfg(d?.supportConfig ?? null))
      .catch(() => setCfg(null));
  }, []);

  // CMS-block trigger delegation — any [data-ast-support] / .agentic-support-trigger opens the popup.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = (e.target as HTMLElement)?.closest?.('[data-ast-support], .agentic-support-trigger');
      if (target) { e.preventDefault(); setOpen(true); }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const reset = useCallback(() => {
    setOpen(false); setReference(null); setSubmitted(false);
    setForm({ department_id: '', subject: '', message: '', name: '', email: '', phone: '' });
  }, []);

  // Once the ticket is in, show the confirmation briefly, then close the popup
  // on its own so the form never just sits there looking unsubmitted.
  useEffect(() => {
    if (!submitted) return;
    const t = setTimeout(() => reset(), 4500);
    return () => clearTimeout(t);
  }, [submitted, reset]);

  const submit = useCallback(async () => {
    if (!cfg) return;
    if (!form.subject.trim() || !form.message.trim()) { showError('Please add a subject and a message.'); return; }
    if (cfg.allow_guests && !form.name.trim()) { showError('Please enter your name.'); return; }
    if (cfg.allow_guests && !form.email.trim()) { showError('Please enter your email so we can reply.'); return; }
    if (!form.phone.trim()) { showError('Please enter your phone number.'); return; }
    setSubmitting(true);
    try {
      const token = cfg.recaptcha_site_key ? await getRecaptchaToken(cfg.recaptcha_site_key) : null;
      const input: Record<string, unknown> = { subject: form.subject.trim(), message: form.message.trim() };
      if (form.department_id) input.department_id = Number(form.department_id);
      if (form.name.trim()) input.name = form.name.trim();
      if (form.email.trim()) input.email = form.email.trim();
      if (form.phone.trim()) input.phone = form.phone.trim();
      if (token) input.recaptcha_token = token;
      // noRetry: the reCAPTCHA token is single-use. The client's transparent
      // retry would replay a spent token (server rejects it → "could not
      // verify"), turning an already-created ticket into a displayed failure.
      const d = await graphqlRequest<{ createSupportTicket: { success: boolean; message?: string; reference?: string } }>(
        CREATE_MUTATION, { input }, { noRetry: true }
      );
      const res = d?.createSupportTicket;
      // Success is driven by `success`, NOT by a non-empty reference — otherwise a
      // blank reference would leave the form on screen as if nothing happened.
      if (res?.success) { setSubmitted(true); setReference(res.reference || null); showSuccess('Support request sent — we’ll be in touch.'); }
      else { showError(res?.message || 'Could not send your request. Please try again.'); }
    } catch {
      showError('Could not send your request. Please try again.');
    } finally { setSubmitting(false); }
  }, [cfg, form]);

  if (!mounted || !cfg?.enabled) return null;

  const accent = (cfg.accent_color?.trim()) ? cfg.accent_color : 'var(--color-primary, #2563eb)';
  // WCAG-readable text on the accent button. White-on-accent often fails 4.5:1
  // (e.g. a mid orange like #e8590c is ~3.4:1) → flagged by Lighthouse. Pick the
  // higher-contrast of black/white when the accent is a literal hex; default to
  // white when it's a CSS var (can't measure at render).
  const onAccent = readableOnAccent(accent);
  const left = (cfg.position || 'bottom-right').includes('left');
  const departments = cfg.departments ?? [];
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 'var(--style-input-radius, 8px)',
    border: '1px solid var(--glass-border, rgba(0,0,0,0.15))', background: 'var(--surface-1, #fff)',
    color: 'var(--text-primary, #111827)', marginTop: 4,
  };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary, #6b7280)', marginBottom: 8 };

  return (
    <>
      {/* Floating launcher — only when the popup is enabled and not hidden on mobile via config */}
      {cfg.popup_enabled && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={cfg.button_text || 'Support'}
          className={cfg.show_on_mobile === false ? 'support-fab support-fab--desktop' : 'support-fab'}
          style={{
            // `bottom` is set in CSS (.support-fab) so a mobile media query can lift
            // the button above the fixed bottom nav bar — see the <style> block below.
            position: 'fixed', [left ? 'left' : 'right']: 20, zIndex: 9998,
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 18px',
            background: accent, color: onAccent, border: 'none', borderRadius: 999, cursor: 'pointer',
            fontSize: 14, fontWeight: 700, boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
          {cfg.button_text || 'Support'}
        </button>
      )}

      {/* Popup */}
      {open && createPortal(
        <div
          role="dialog" aria-modal="true" aria-label={cfg.heading || 'Contact support'}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div onClick={reset} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }} />
          <div
            style={{
              position: 'relative', width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto',
              background: 'var(--surface-1, #fff)', color: 'var(--text-primary, #111827)',
              borderRadius: 'var(--style-card-radius, 14px)', boxShadow: '0 24px 70px rgba(0,0,0,0.35)',
              border: '1px solid var(--glass-border, rgba(0,0,0,0.1))',
            }}
          >
            {/* Header */}
            <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--glass-border, rgba(0,0,0,0.1))', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{cfg.heading || 'How can we help?'}</h2>
                {cfg.intro && <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary, #6b7280)', lineHeight: 1.5 }}>{cfg.intro}</p>}
              </div>
              <button type="button" onClick={reset} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, lineHeight: 1, color: 'var(--text-secondary, #6b7280)' }}>×</button>
            </div>

            {/* Body */}
            <div style={{ padding: 20 }}>
              {submitted ? (
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                  <div style={{ width: 48, height: 48, margin: '0 auto 14px', borderRadius: '50%', background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={onAccent} strokeWidth="2.5" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                  </div>
                  <p style={{ margin: '0 0 6px', fontWeight: 700 }}>Thanks — your request is in.</p>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
                    {reference
                      ? <>Your reference is <strong>{reference}</strong>. We’ll reply by email.</>
                      : <>We’ll reply by email shortly.</>}
                  </p>
                  <button type="button" onClick={reset} style={{ marginTop: 18, padding: '10px 18px', background: accent, color: onAccent, border: 'none', borderRadius: 'var(--style-btn-radius, 8px)', cursor: 'pointer', fontWeight: 700 }}>Done</button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {departments.length > 0 && (
                    <label style={labelStyle}>Department
                      <select value={form.department_id} onChange={set('department_id')} style={fieldStyle}>
                        <option value="">General enquiry</option>
                        {departments.map((d) => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
                      </select>
                    </label>
                  )}
                  {cfg.allow_guests && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <label style={labelStyle}>Name *<input type="text" value={form.name} onChange={set('name')} style={fieldStyle} autoComplete="name" required /></label>
                      <label style={labelStyle}>Email *<input type="email" value={form.email} onChange={set('email')} style={fieldStyle} autoComplete="email" required /></label>
                    </div>
                  )}
                  <label style={labelStyle}>Phone *<input type="tel" value={form.phone} onChange={set('phone')} style={fieldStyle} autoComplete="tel" required /></label>
                  <label style={labelStyle}>Subject *<input type="text" value={form.subject} onChange={set('subject')} style={fieldStyle} /></label>
                  <label style={labelStyle}>How can we help? *<textarea value={form.message} onChange={set('message')} rows={5} style={{ ...fieldStyle, resize: 'vertical' }} /></label>
                  <button type="button" onClick={() => void submit()} disabled={submitting} style={{ padding: '12px 18px', background: accent, color: onAccent, border: 'none', borderRadius: 'var(--style-btn-radius, 8px)', cursor: submitting ? 'default' : 'pointer', fontWeight: 700, opacity: submitting ? 0.7 : 1 }}>
                    {submitting ? 'Sending…' : 'Send request'}
                  </button>
                  {cfg.recaptcha_site_key && (
                    <p style={{ margin: 0, fontSize: 10, color: 'var(--text-secondary, #94a3b8)', textAlign: 'center' }}>Protected by reCAPTCHA.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        .support-fab{ bottom: 20px; }
        /* On mobile the fixed bottom nav bar (~68px tall + safe-area inset) is present;
           lift the support button above it so it never covers the Cart / Quote icons.
           The bar is md:hidden, so desktop keeps the 20px bottom. */
        @media (max-width: 767px){ .support-fab{ bottom: calc(80px + env(safe-area-inset-bottom, 0px)); } }
        @media (max-width: 640px){ .support-fab--desktop{ display:none !important; } }
      `}</style>
    </>
  );
}
